import { CompanyTypeKind } from "@custom-types/base-types";

interface CompanyTypeConfig {
  /** Label for the company type */
  label: string;

  /** Theme colour of the dot that marks the type in the type dropdown */
  color: string;

  /** The chip colour of the type, which follows the colour scheme */
  chipColor: "default" | "primary" | "success" | "warning";
}

/** Shared style for internal company types */
const INTERNAL_STYLE: Omit<CompanyTypeConfig, "label"> = {
  color: "warning.main",
  chipColor: "warning",
};

/**
 * Company type configuration with colors:
 * - Customer: Blue
 * - Partner/Distributor: Green
 * - Internal (R&D, Sales, Support): Yellow
 * - Unspecified: Gray (display only)
 */
export const COMPANY_TYPE_CONFIG: Record<CompanyTypeKind, CompanyTypeConfig> = {
  customer: {
    label: "Customer",
    color: "primary.main",
    chipColor: "primary",
  },
  ["partner_distributor"]: {
    label: "Partner / Distributor",
    color: "success.main",
    chipColor: "success",
  },
  ["internal_rnd"]: { label: "Internal R&D", ...INTERNAL_STYLE },
  ["internal_sales"]: { label: "Internal Sales", ...INTERNAL_STYLE },
  ["internal_support"]: { label: "Internal Support", ...INTERNAL_STYLE },
  unspecified: {
    label: "Unspecified",
    color: "text.secondary",
    chipColor: "default",
  },
};

/** The workspace types that FARO runs for itself, rather than for a paying customer */
const INTERNAL_COMPANY_TYPES: readonly CompanyTypeKind[] = [
  "internal_rnd",
  "internal_sales",
  "internal_support",
];

/** Whether the workspace belongs to FARO itself instead of to a customer */
export function isInternalCompanyType(
  type: CompanyTypeKind | undefined
): boolean {
  return type !== undefined && INTERNAL_COMPANY_TYPES.includes(type);
}
